import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { registrarAbertura } from "../../push";
import { CartaoDoApp } from "../../push/CartaoDoApp";
import useUserStore from "../../store/user";
import { idDoPerfil, perfisDoCliente, type PerfilDoCliente } from "../../utils/perfis-do-cliente";
import { RelatorioCompleto } from "../relatorio/pages";
import { Largura } from "../relatorio/styles";
import { MenuDoCliente } from "./menu-do-cliente";

/**
 * Tela do cliente (/home, onde o app instalado abre): o relatório completo da
 * empresa escolhida, com a troca de empresa no topo. `?perfil=<id>` abre direto
 * uma empresa da lista (links de notificação e de /dashboard/:id antigos),
 * `?secao=` rola até a seção e `?n=<ref>` conta a abertura da notificação.
 */
export const InicioDoCliente = () => {
  const { user, profiles, profileAccesses, setUser, clearUser } = useUserStore();
  const navigate = useNavigate();
  const [busca, setBusca] = useSearchParams();
  const perfis = useMemo(() => perfisDoCliente(profiles, profileAccesses), [profiles, profileAccesses]);
  const pedido = busca.get("perfil");
  // Lidos uma vez: a URL é limpa logo depois, mas o relatório ainda precisa rolar até a seção.
  const [secao] = useState(() => busca.get("secao"));
  const escolhido = idDoPerfil(user?.businessProfiles?.[0]);
  const atual = perfis.some((perfil) => perfil.id === escolhido) || (escolhido && perfis.length === 0) ? escolhido : perfis[0]?.id ?? "";

  const trocar = (perfil: PerfilDoCliente) => {
    if (!user) return;
    setUser({ ...user, businessProfiles: [perfil.id], selectedProfileAccessLevel: perfil.nivel });
    window.scrollTo({ top: 0 });
  };

  // Abre a empresa pedida no link, se ela for do cliente; registra a abertura da
  // notificação; depois limpa a URL (recarregar não conta de novo).
  useEffect(() => {
    const ref = busca.get("n");
    if (ref) registrarAbertura(ref);
    if (pedido) {
      const alvo = perfis.find((perfil) => perfil.id === pedido);
      if (alvo && alvo.id !== escolhido) trocar(alvo);
    }
    if (!pedido && !ref && !busca.get("secao")) return;
    ["perfil", "n", "secao"].forEach((chave) => busca.delete(chave));
    setBusca(busca, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sem empresa gravada (sessão antiga): fixa a primeira para o resto do app.
  useEffect(() => {
    if (user && atual && atual !== escolhido && !pedido) {
      const perfil = perfis.find((item) => item.id === atual);
      if (perfil) trocar(perfil);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atual, escolhido]);

  if (!user) return <Navigate to="/login" replace />;
  if (!atual) return <Navigate to="/select-profile" replace />;

  const sair = () => {
    clearUser();
    localStorage.removeItem("user-storage");
    navigate("/login");
  };

  return (
    <RelatorioCompleto
      key={atual}
      profileId={atual}
      secao={secao}
      topo={
        <MenuDoCliente
          perfis={perfis}
          atual={atual}
          aoTrocar={trocar}
          aoAbrirPainel={() => navigate("/painel")}
          aoSair={sair}
        />
      }
      aposCapa={
        <Largura style={{ marginTop: 16 }}>
          <CartaoDoApp />
        </Largura>
      }
    />
  );
};
